import React from 'react';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { createRun, deleteRun, listRuns, publishRun, getRun, Run } from '../run-outstanding/api';
import { Upload, FileSpreadsheet, X, Trash2, Send, AlertCircle, Eye, Loader2 } from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "components/ui/dialog";
import dayjs from 'dayjs';
import { cn } from 'lib/utils';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);

type FileUploadState = {
    file: File | null;
    stock_file: File | null;
};

const FILE_LABELS: Record<keyof FileUploadState, string> = {
    file: 'Unbilled Report',
    stock_file: 'CN Hold / Stock Report',
};

const UnbilledRunsPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();

    const [selectedDate, setSelectedDate] = React.useState('');
    const [files, setFiles] = React.useState<FileUploadState>({
        file: null,
        stock_file: null,
    });
    const [uploading, setUploading] = React.useState(false);
    const [uploadError, setUploadError] = React.useState('');
    const [uploadSuccess, setUploadSuccess] = React.useState('');

    const [runs, setRuns] = React.useState<Run[]>([]);
    const [error, setError] = React.useState<string>('');
    const [loading, setLoading] = React.useState(true);
    const [actionLoading, setActionLoading] = React.useState<Record<string, boolean>>({});

    const [selectedErrorRun, setSelectedErrorRun] = React.useState<Run | null>(null);
    const [selectedPublishRun, setSelectedPublishRun] = React.useState<Run | null>(null);

    const fileInputRefs = React.useRef<Record<keyof FileUploadState, HTMLInputElement | null>>({
        file: null,
        stock_file: null,
    });

    const allFilesUploaded = (Object.keys(files) as Array<keyof FileUploadState>)
        .every(key => files[key] !== null);

    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Settings', href: '#' },
            { label: 'Run Unbilled Report' },
        ]);
    }, [setBreadcrumbs]);

    const fetchRuns = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await listRuns('unbilled');
            const sorted = [...data.runs].sort((a, b) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            setRuns(sorted);
        } catch (err: any) {
            console.error('Failed to fetch runs:', err);
            setRuns([]);
            setError(err?.response?.data?.detail || err?.message || 'Failed to fetch runs');
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchRuns();
    }, [fetchRuns]);

    React.useEffect(() => {
        const activeRuns = runs.filter(run => run.status === 'running' || run.status === 'queued');
        if (activeRuns.length === 0) return;

        const poll = async () => {
            const updates = await Promise.all(
                activeRuns.map(async (run) => {
                    try {
                        const updatedRun = await getRun(run.id);
                        if (updatedRun.status !== run.status) {
                            return updatedRun;
                        }
                    } catch (e) {
                        console.error(`Failed to poll run ${run.id}`, e);
                    }
                    return null;
                })
            );

            const runsToUpdate = updates.filter((r): r is Run => r !== null);
            if (runsToUpdate.length > 0) {
                setRuns(prevRuns => prevRuns.map(run => {
                    const updated = runsToUpdate.find(u => u.id === run.id);
                    return updated || run;
                }));
            }
        };

        const intervalId = setInterval(poll, 1000);
        return () => clearInterval(intervalId);
    }, [runs]);

    const getFinancialYear = (date: Date): string => {
        const month = date.getMonth();
        const year = date.getFullYear();
        return month >= 3 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
    };

    const handleRun = async () => {
        setUploadError('');
        setUploadSuccess('');

        if (!selectedDate) {
            setUploadError('Please select a date');
            return;
        }

        const todayStr = dayjs().format('YYYY-MM-DD');
        if (selectedDate > todayStr) {
            setUploadError('Date cannot be in the future');
            return;
        }

        const missingFiles = (Object.keys(files) as Array<keyof FileUploadState>)
            .filter(key => files[key] === null)
            .map(key => FILE_LABELS[key]);
        if (missingFiles.length > 0) {
            setUploadError(`Please select: ${missingFiles.join(', ')}`);
            return;
        }

        setUploading(true);
        try {
            const dateObj = new Date(selectedDate);
            const fy = getFinancialYear(dateObj);

            const formData = new FormData();
            formData.append('pipeline', 'unbilled');
            formData.append('year', fy);
            formData.append('month', (dateObj.getMonth() + 1).toString());
            formData.append('day', dateObj.getDate().toString());
            (Object.keys(files) as Array<keyof FileUploadState>).forEach((key) => {
                const selected = files[key];
                if (selected) formData.append(key, selected);
            });

            await createRun(formData);
            setUploadSuccess('Run created successfully!');

            setSelectedDate('');
            setFiles({ file: null, stock_file: null });
            (Object.keys(fileInputRefs.current) as Array<keyof FileUploadState>).forEach((key) => {
                const input = fileInputRefs.current[key];
                if (input) input.value = '';
            });

            fetchRuns();
        } catch (error: any) {
            setUploadError(error.message || 'Failed to create run');
        } finally {
            setUploading(false);
        }
    };

    const openPublishDialog = (run: Run) => {
        setSelectedPublishRun(run);
    };

    const handlePublishConfirm = async () => {
        if (!selectedPublishRun) return;

        const runId = selectedPublishRun.id;
        setActionLoading(prev => ({ ...prev, [runId]: true }));
        try {
            await publishRun(runId);
            fetchRuns();
            setSelectedPublishRun(null);
        } catch (error: any) {
            alert(`Failed to publish run: ${error.message}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [runId]: false }));
        }
    };

    const handleDelete = async (runId: string) => {
        if (!window.confirm('Are you sure you want to delete this run?')) return;

        setActionLoading(prev => ({ ...prev, [runId]: true }));
        try {
            await deleteRun(runId);
            fetchRuns();
        } catch (error: any) {
            console.error(error);
            alert(`Failed to delete run: ${error.message}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [runId]: false }));
        }
    };

    const handleFileChange = (fileKey: keyof FileUploadState) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        // backend only accepts .xlsx
        if (!selectedFile.name.endsWith('.xlsx')) {
            setUploadError(`${FILE_LABELS[fileKey]} must be an Excel file (.xlsx)`);
            setFiles(prev => ({ ...prev, [fileKey]: null }));
            const input = fileInputRefs.current[fileKey];
            if (input) input.value = '';
            return;
        }

        setFiles(prev => ({ ...prev, [fileKey]: selectedFile }));
        setUploadError('');
    };

    const removeFile = (fileKey: keyof FileUploadState) => {
        setFiles(prev => ({ ...prev, [fileKey]: null }));
        const input = fileInputRefs.current[fileKey];
        if (input) input.value = '';
    };

    return (
        <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-6 sm:px-8 py-6">
            <div className="max-w-3xl">
                <Card className="border-border">
                    <CardHeader className="pb-3 px-6 pt-5">
                        <CardTitle className="text-lg font-semibold flex items-center gap-2">
                            <Upload className="h-5 w-5 text-muted-foreground" />
                            Upload Unbilled Report
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6">
                        <div className="flex items-start gap-4">
                            <div className="flex-1 max-w-[200px]">
                                <Input
                                    type="date"
                                    value={selectedDate}
                                    max={dayjs().format('YYYY-MM-DD')}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="h-10"
                                    disabled={uploading}
                                />
                            </div>

                            <div className="flex-1 grid grid-cols-2 gap-3">
                                {(Object.keys(files) as Array<keyof FileUploadState>).map((fileKey) => (
                                    <div key={fileKey}>
                                        {!files[fileKey] ? (
                                            <div
                                                onClick={() => fileInputRefs.current[fileKey]?.click()}
                                                className="h-10 border border-dashed border-input rounded-md flex items-center px-4 cursor-pointer hover:bg-accent/50 transition-colors text-sm text-muted-foreground"
                                            >
                                                <FileSpreadsheet className="h-4 w-4 mr-2 flex-shrink-0" />
                                                <span className="truncate">{FILE_LABELS[fileKey]}...</span>
                                            </div>
                                        ) : (
                                            <div className="h-10 border border-dashed border-blue-200 bg-blue-50/50 rounded-md flex items-center justify-between px-3 text-sm">
                                                <div className="flex items-center gap-2 overflow-hidden">
                                                    <FileSpreadsheet className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                                    <span className="truncate font-medium text-blue-700">{files[fileKey]!.name}</span>
                                                </div>
                                                <button
                                                    onClick={() => removeFile(fileKey)}
                                                    className="ml-2 p-1 hover:bg-blue-100 rounded-full text-blue-600 transition-colors"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        )}
                                        <input
                                            ref={(el) => { fileInputRefs.current[fileKey] = el; }}
                                            type="file"
                                            accept=".xlsx"
                                            onChange={handleFileChange(fileKey)}
                                            className="hidden"
                                            disabled={uploading}
                                        />
                                    </div>
                                ))}
                            </div>

                            <Button
                                onClick={handleRun}
                                disabled={uploading || !allFilesUploaded || !selectedDate}
                                className="h-10 min-w-[100px]"
                            >
                                {uploading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                ) : (
                                    'Run'
                                )}
                            </Button>
                        </div>

                        {uploadError && (
                            <p className="text-sm text-destructive mt-3">{uploadError}</p>
                        )}
                        {uploadSuccess && (
                            <p className="text-sm text-green-600 mt-3">{uploadSuccess}</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            <div className="space-y-4">
                <h2 className="text-lg font-semibold tracking-tight">Previous Runs</h2>
                <div className="rounded-md border bg-card">
                    {error ? (
                        <div className="p-4">
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-red-600 text-sm">{error}</p>
                            </div>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/50">
                                    <TableHead className="w-[180px]">Date</TableHead>
                                    <TableHead>Financial Year</TableHead>
                                    <TableHead>Created By</TableHead>
                                    <TableHead>Created At</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>State</TableHead>
                                    <TableHead className="text-right w-[220px]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-24 text-center">
                                            <div className="flex items-center justify-center text-muted-foreground">
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mr-2" />
                                                Loading runs...
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : runs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                                            No runs found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    runs.map((run) => (
                                        <TableRow key={run.id}>
                                            <TableCell className="font-medium">
                                                {dayjs().month(run.month - 1).format('MMMM')} {run.day}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {run.year}
                                            </TableCell>
                                            <TableCell>
                                                {run.created_by}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {dayjs(run.created_at).format('MMM D, YYYY h:mm A')}
                                            </TableCell>
                                            <TableCell>
                                                <span className={cn(
                                                    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                                                    run.status === 'succeeded' ? "bg-green-50 text-green-700 ring-green-600/20" :
                                                        run.status === 'running' ? "bg-blue-50 text-blue-700 ring-blue-600/20" :
                                                            run.status === 'queued' ? "bg-yellow-50 text-yellow-700 ring-yellow-600/20" :
                                                                "bg-red-50 text-red-700 ring-red-600/20"
                                                )}>
                                                    {(run.status === 'running' || run.status === 'queued') && (
                                                        <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                                                    )}
                                                    {run.status === 'succeeded' ? 'Succeeded' :
                                                        run.status === 'running' ? 'Running' :
                                                            run.status === 'queued' ? 'Queued' : 'Failed'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <span className={cn(
                                                    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                                                    run.state === 'published' ? "bg-purple-50 text-purple-700 ring-purple-600/20" :
                                                        run.state === 'deleted' ? "bg-gray-50 text-gray-700 ring-gray-600/20" :
                                                            "bg-amber-50 text-amber-700 ring-amber-600/20"
                                                )}>
                                                    {run.state === 'published' ? 'Published' :
                                                        run.state === 'deleted' ? 'Deleted' : 'Draft'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2 min-h-[28px]">
                                                    {run.status === 'failed' && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 text-xs border-destructive text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5 px-2.5"
                                                            onClick={() => setSelectedErrorRun(run)}
                                                        >
                                                            <AlertCircle className="h-3.5 w-3.5" />
                                                            View Error Log
                                                        </Button>
                                                    )}

                                                    {run.status === 'succeeded' && run.state === 'draft' && (
                                                        <>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-7 text-xs gap-1.5 px-2.5 border-green-200 text-green-700 hover:text-green-800 hover:bg-green-50 hover:border-green-300"
                                                                onClick={() => openPublishDialog(run)}
                                                                disabled={actionLoading[run.id]}
                                                            >
                                                                <Send className="h-3 w-3" />
                                                                Publish
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-7 text-xs gap-1.5 px-2.5 border-red-200 text-red-700 hover:text-red-800 hover:bg-red-50 hover:border-red-300"
                                                                onClick={() => handleDelete(run.id)}
                                                                disabled={actionLoading[run.id]}
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                                Delete
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>

            <Dialog open={!!selectedErrorRun} onOpenChange={(open) => !open && setSelectedErrorRun(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertCircle className="h-5 w-5" />
                            Run Failed
                        </DialogTitle>
                        <DialogDescription>
                            Run ID: {selectedErrorRun?.id}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2 bg-muted/50 rounded-md p-4 max-h-[400px] overflow-y-auto">
                        <pre className="text-sm font-mono whitespace-pre-wrap text-foreground">
                            {selectedErrorRun?.error || 'No error details available.'}
                        </pre>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!selectedPublishRun} onOpenChange={(open) => !open && setSelectedPublishRun(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Publish</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to publish this run? Once published, the data will be visible to all users.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedPublishRun(null)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handlePublishConfirm}
                            disabled={!!selectedPublishRun && actionLoading[selectedPublishRun.id]}
                        >
                            {!!selectedPublishRun && actionLoading[selectedPublishRun.id] ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Publishing...
                                </>
                            ) : (
                                'Publish'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UnbilledRunsPage;
